const decimal = (value) => typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value);

// Usage quantities remain decimal strings. Numbers below are plot coordinates only.
export function sumUsage(values) {
  if (!values.length || values.some((value) => !decimal(value))) return null;
  const scale = values.reduce((maximum, value) => Math.max(maximum, (value.split('.')[1] || '').length), 0);
  const sum = values.reduce((total, value) => {
    const [whole, fraction = ''] = value.split('.');
    return total + BigInt(whole + fraction.padEnd(scale, '0'));
  }, 0n).toString().padStart(scale + 1, '0');
  return scale ? `${sum.slice(0, -scale)}.${sum.slice(-scale)}` : sum;
}

export function formatUsageQuantity(value) {
  if (!decimal(value)) return 'Unknown';
  const [whole, fraction = ''] = value.split('.');
  const tail = fraction.replace(/0+$/, '');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${tail ? `.${tail}` : ''}`;
}

export function isSummaryUsage(view) {
  return Array.isArray(view?.data?.totals) && Array.isArray(view?.data?.buckets) &&
    view.data.buckets.every((bucket) => Array.isArray(bucket.summaries)) &&
    typeof view.data.pagination?.hasMore === 'boolean';
}

export function usageCoverageText(view) {
  if (!isSummaryUsage(view)) return 'Usage summary unavailable · coverage unknown';
  const states = [view.dataQuality, ...view.data.totals.map((row) => row.coverage?.status)];
  if (states.some((state) => !['COMPLETE', 'PARTIAL'].includes(state))) return 'Coverage unknown';
  return states.includes('PARTIAL') ? 'Coverage partial' : 'Coverage complete';
}

const sameTime = (a, b) => Number.isFinite(Date.parse(a)) && Date.parse(a) === Date.parse(b);
const fail = () => { throw new Error('Usage response changed scope or snapshot; refresh to retry.'); };

export async function loadUsagePages(client, workerId, environment, query = {}, options = {}) {
  const first = await client.getBillingUsage(workerId, environment, query, options);
  if ((first?.workerId && first.workerId !== workerId) || (first?.environment && first.environment !== environment) ||
      (first?.snapshotTime && query.snapshotTime && !sameTime(first.snapshotTime, query.snapshotTime))) fail();
  if (!isSummaryUsage(first)) {
    if (first?.data?.totals || first?.data?.pagination || first?.data?.buckets?.some((b) => 'summaries' in b)) fail();
    // The existing read still works against an older server, but its finite facts
    // cannot establish total usage or COMPLETE coverage. Diagnostics stay separate.
    return { ...first, dataQuality: 'INDETERMINATE', completeThrough: null };
  }
  if (first.workerId !== workerId || first.environment !== environment || !first.snapshotId ||
      !sameTime(first.snapshotTime, query.snapshotTime || first.snapshotTime) ||
      !sameTime(first.data.from, query.from || first.data.from) || !sameTime(first.data.to, query.to || first.data.to) ||
      first.data.resourceId !== (query.resourceId || null) || first.data.metric !== (query.metric || null)) fail();
  if (first.data.totals.some((row) => !row.metric || (row.quantity !== null && !decimal(row.quantity)) || !row.coverage?.status)) fail();
  const buckets = first.data.buckets.map((bucket) => ({ ...bucket, summaries: [] }));
  const groups = new Set(), cursors = new Set();
  const proof = (view) => JSON.stringify([view.workerId, view.environment, view.snapshotId, view.snapshotTime,
    view.completeThrough, view.dataQuality, view.data.from, view.data.to, view.data.resourceId, view.data.metric,
    view.data.bucketSeconds, view.data.bucketCount, view.data.gapCount, view.data.partialCount, view.data.totals,
    view.data.buckets.map(({ start, end, status }) => [start, end, status])]);
  const expected = proof(first);
  let page = first;
  while (true) {
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!isSummaryUsage(page) || proof(page) !== expected) fail();
    page.data.buckets.forEach((bucket, index) => {
      for (const row of bucket.summaries) {
        if (!row.resourceKey || !/^[A-Z][A-Z0-9_]*$/.test(row.metric) || (row.quantity !== null && !decimal(row.quantity)) ||
            !row.sourceSelection?.kind || !row.coverage?.status) fail();
        const key = JSON.stringify([bucket.start, row.resourceKey, row.metric]);
        if (groups.has(key)) fail();
        groups.add(key);
        buckets[index].summaries.push(row);
      }
    });
    if (!page.data.pagination.hasMore) break;
    const cursor = page.data.pagination.nextCursor;
    if (typeof cursor !== 'string' || !cursor || cursors.has(cursor)) fail();
    cursors.add(cursor);
    page = await client.getBillingUsage(workerId, environment, {
      ...query, snapshotTime: first.snapshotTime, from: first.data.from, to: first.data.to, cursor,
    }, options);
  }
  return { ...first, data: { ...first.data, buckets, pagination: { hasMore: false, nextCursor: null } } };
}

export function metricSeries(buckets = [], metric, resourceId) {
  return buckets.map((bucket) => {
    const summaries = Array.isArray(bucket.summaries);
    const rows = (summaries ? bucket.summaries : bucket.facts || []).filter((row) =>
      row.metric === metric && (!resourceId || row.resourceId === resourceId));
    const exactValue = bucket.status === 'GAP' || !summaries ? null : sumUsage(rows.map((row) => row.quantity));
    return { start: bucket.start, status: summaries ? bucket.status : bucket.status === 'GAP' ? 'GAP' : 'PARTIAL',
      exactValue, value: exactValue === null ? null : Number(exactValue), present: rows.length > 0 };
  });
}

export function resourceTotals(buckets = [], resourceId) {
  const metrics = new Map();
  for (const bucket of buckets) {
    if (bucket.status === 'GAP') continue;
    const summaries = Array.isArray(bucket.summaries);
    for (const row of summaries ? bucket.summaries : bucket.facts || []) {
      if (resourceId && row.resourceId !== resourceId) continue;
      const values = metrics.get(row.metric) || [];
      values.push(summaries ? row.quantity : null);
      metrics.set(row.metric, values);
    }
  }
  return new Map([...metrics].map(([metric, values]) => [metric, sumUsage(values)]));
}

export function scopedUsageTotals(view, resourceId) {
  if (isSummaryUsage(view) && view.data.resourceId === resourceId) {
    return new Map(view.data.totals.map((row) => [row.metric, decimal(row.quantity) ? row.quantity : null]));
  }
  return resourceTotals(view?.data?.buckets || [], resourceId);
}
